<template>
  <div class="superadmin-data-table">
    <b-card>
      <!-- Table Header -->
      <template #header>
        <b-row class="align-items-center">
          <b-col>
            <h5 class="mb-0">
              <i :class="headerIcon" class="mr-2"></i>
              {{ title }}
              <b-badge v-if="totalItems > 0" variant="primary" class="ml-2">
                {{ totalItems.toLocaleString() }} {{ totalItems === 1 ? 'result' : 'results' }}
              </b-badge>
            </h5>
          </b-col>
          <b-col cols="auto">
            <b-button-group size="sm">
              <b-button
                variant="outline-secondary"
                @click="refreshData"
                :disabled="isLoading"
              >
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
              </b-button>
              
              <b-button
                variant="outline-primary"
                @click="exportData"
                :disabled="isLoading || items.length === 0"
              >
                <i class="fas fa-download mr-1"></i>
                Export
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>
      </template>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2 text-muted">Loading {{ title.toLowerCase() }}...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="items.length === 0" class="text-center py-5">
        <i :class="headerIcon" class="fa-3x text-muted mb-3"></i>
        <h5 class="text-muted">No {{ title.toLowerCase() }} found</h5>
        <p class="text-muted">Try adjusting your search criteria or clear filters to see more results.</p>
      </div>

      <!-- Data Table -->
      <div v-else>
        <!-- Results Summary -->
        <b-row class="mb-3">
          <b-col>
            <small class="text-muted">
              Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} - {{ Math.min(currentPage * itemsPerPage, totalItems) }} 
              of {{ totalItems.toLocaleString() }} {{ title.toLowerCase() }}
            </small>
          </b-col>
          <b-col cols="auto">
            <b-form-group class="mb-0">
              <b-form-select
                v-model="itemsPerPage"
                size="sm"
                :options="perPageOptions"
                @change="updateItemsPerPage"
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          :current-page="currentPage"
          :per-page="itemsPerPage"
          responsive
          striped
          hover
          show-empty
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          @sort-changed="onSortChanged"
          class="mb-0"
        >
          <!-- Custom slots for specific data types -->
          <template #cell(status)="{ value }">
            <b-badge 
              :variant="getStatusVariant(value)"
              class="px-2 py-1"
            >
              {{ getStatusText(value) }}
            </b-badge>
          </template>

          <template #cell(userType)="{ value }">
            <b-badge
              :variant="value === 1 ? 'info' : 'secondary'"
              class="px-2 py-1"
            >
              {{ value === 1 ? 'Doctor' : 'Staff' }}
            </b-badge>
          </template>

          <template #cell(hasLicense)="{ value }">
            <i 
              :class="value ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-muted'"
              :title="value ? 'Has License' : 'No License'"
            ></i>
          </template>

          <template #cell(totalBranches)="{ value }">
            <b-badge variant="outline-primary" class="px-2 py-1">
              {{ value }} {{ value === 1 ? 'Branch' : 'Branches' }}
            </b-badge>
          </template>

          <template #cell(totalMembers)="{ value }">
            <b-badge variant="outline-success" class="px-2 py-1">
              {{ value }} {{ value === 1 ? 'Member' : 'Members' }}
            </b-badge>
          </template>

          <!--
            แพ็กที่สาขานั้นถืออยู่ — value เป็น null ได้เมื่ออ่านข้อมูล billing ไม่สำเร็จ
            ซึ่งต่างจากแพ็กฟรี (planCode: "FREE") ต้องไม่แสดงสองกรณีนี้เหมือนกัน
          -->
          <template #cell(plan)="{ value }">
            <b-badge v-if="!value" variant="light" class="px-2 py-1" title="อ่านข้อมูลแพ็กไม่ได้">?</b-badge>
            <span v-else>
              <b-badge :variant="value.planCode === 'FREE' ? 'secondary' : 'primary'" class="px-2 py-1">
                {{ value.planCode }}
              </b-badge>
              <i
                v-if="value.recurringCancellationPending"
                class="fas fa-exclamation-triangle text-warning ml-1"
                v-b-tooltip.hover
                title="ผู้ให้บริการยังถือแผนตัดเงินอยู่ ต้องไปยกเลิกในพอร์ทัล"
              ></i>
            </span>
          </template>

          <template #cell(creationDt)="{ value }">
            <small class="text-muted">
              {{ formatDate(value) }}
            </small>
          </template>

          <template #cell(lastActivity)="{ value }">
            <small class="text-muted">
              {{ value ? formatDate(value) : '-' }}
            </small>
          </template>

          <!-- Action buttons -->
          <template #cell(actions)="{ item }">
            <b-button-group size="sm">
              <b-button
                variant="outline-primary"
                @click="viewDetails(item)"
                v-b-tooltip.hover
                title="View Details"
              >
                <i class="fas fa-eye"></i>
              </b-button>
              
              <b-button
                v-if="showStatusActions && item.statusId !== undefined"
                :variant="item.statusId === 1 ? 'outline-warning' : 'outline-success'"
                @click="toggleStatus(item)"
                v-b-tooltip.hover
                :title="item.statusId === 1 ? 'Deactivate User' : 'Activate User'"
              >
                <i :class="item.statusId === 1 ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
              </b-button>
              
              <b-button
                v-if="showRefundAction && item.branchId"
                variant="outline-danger"
                @click="$emit('refund', item)"
                v-b-tooltip.hover
                title="คืนเงินและเพิกถอนสิทธิ์"
              >
                <i class="fas fa-undo"></i>
              </b-button>

              <b-button
                variant="outline-info"
                @click="exportItem(item)"
                v-b-tooltip.hover
                title="Export Data"
              >
                <i class="fas fa-download"></i>
              </b-button>
            </b-button-group>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row class="mt-3 align-items-center">
          <b-col>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              :limit="maxPaginationLinks"
              @change="onPageChanged"
              size="sm"
              class="mb-0"
              align="center"
            ></b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: "SuperadminDataTable",
  props: {
    // ปิดไว้ตามปริยายโดยตั้งใจ — ตารางนี้ใช้ร่วมกับหน้าผู้ใช้ด้วย ปุ่มเรื่องเงินต้องไม่
    // โผล่ที่นั่นเพราะแถวของมันไม่มี branchId และไม่เกี่ยวกับ billing เลย
    showRefundAction: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    headerIcon: {
      type: String,
      default: 'fas fa-table'
    },
    items: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      required: true
    },
    totalItems: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 20
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: String,
      default: null
    },
    sortDesc: {
      type: Boolean,
      default: false
    },
    showStatusActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxPaginationLinks: 5,
      perPageOptions: [
        { value: 10, text: '10 per page' },
        { value: 20, text: '20 per page' },
        { value: 50, text: '50 per page' },
        { value: 100, text: '100 per page' }
      ]
    };
  },
  methods: {
    viewDetails(item) {
      this.$emit('view-details', item);
    },
    
    toggleStatus(item) {
      this.$emit('toggle-status', item);
    },
    
    exportItem(item) {
      this.$emit('export-item', item);
    },
    
    exportData() {
      this.$emit('export-data');
    },
    
    refreshData() {
      this.$emit('refresh');
    },
    
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
    
    updateItemsPerPage(perPage) {
      this.$emit('per-page-changed', perPage);
    },
    
    onSortChanged(ctx) {
      this.$emit('sort-changed', {
        sortBy: ctx.sortBy,
        sortDesc: ctx.sortDesc
      });
    },
    
    getStatusVariant(status) {
      return status === 1 ? 'success' : 'secondary';
    },
    
    getStatusText(status) {
      return status === 1 ? 'Active' : 'Inactive';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return moment(dateStr).format('MMM DD, YYYY HH:mm');
    }
  }
};
</script>

<style scoped>
.superadmin-data-table .card {
  border: 1px solid #dee2e6;
}

.superadmin-data-table .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.superadmin-data-table .table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa;
}

.superadmin-data-table .table td {
  vertical-align: middle;
}

.superadmin-data-table .table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.superadmin-data-table .btn-group-sm .btn {
  padding: 0.25rem 0.375rem;
}

.superadmin-data-table .badge {
  font-size: 0.75rem;
}

.superadmin-data-table .pagination {
  justify-content: center;
}

.superadmin-data-table .fa-3x {
  font-size: 3rem;
}

/* Loading spinner animation */
.superadmin-data-table .fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Custom badge styles */
.superadmin-data-table .badge-outline-primary {
  color: #007bff;
  background-color: transparent;
  border: 1px solid #007bff;
}

.superladmin-data-table .badge-outline-success {
  color: #28a745;
  background-color: transparent;
  border: 1px solid #28a745;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .superadmin-data-table .btn-group-sm .btn {
    padding: 0.125rem 0.25rem;
  }
  
  .superadmin-data-table .btn-group-sm .btn i {
    font-size: 0.75rem;
  }
}
</style>